import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getCommunitySolutions } from '../services/authApi';
import useEscapeToClose from '../hooks/useEscapeToClose';
import CardArt from './CardArt';
import { IconCommunity, IconInfo, IconEye } from './icons';
import colors from '../theme/colors';
import NodeCardPreview from './NodeCardPreview';

// ── ZzzZZZ sleep icon ────────────────────────────────────────────────────────
const ZzzIcon = () => (
  <svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 32, height: 32, flexShrink: 0 }}>
    <text x="2" y="18" fontSize="10" fill={colors.base.accentPrimary} opacity="0.6" fontWeight="bold">z</text>
    <text x="10" y="27" fontSize="13" fill={colors.base.accentPrimary} opacity="0.8" fontWeight="bold">z</text>
    <text x="21" y="38" fontSize="17" fill={colors.base.accentPrimary} fontWeight="bold">Z</text>
  </svg>
);

// ── Rising sun icon — start of the day, before the first idea ────────────────
const SunIcon = () => (
  <svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 32, height: 32, flexShrink: 0 }}>
    <circle cx="25" cy="27" r="8" stroke={colors.base.accentPrimary} strokeWidth="2.5" opacity="0.9" />
    <g stroke={colors.base.accentPrimary} strokeWidth="2.5" strokeLinecap="round" opacity="0.7">
      <path d="M25 10v5M25 39v5M8 27h5M37 27h5M13 15l3.5 3.5M33.5 38.5 37 42M37 15l-3.5 3.5M16.5 38.5 13 42" />
    </g>
  </svg>
);

// ── Styled components ────────────────────────────────────────────────────────
const TriggerBtn = styled.button`
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 20px;
  padding: 6px 14px;
  color: rgba(255, 255, 255,0.85);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  /* Push away from the subtitle, pull toward the header's bottom edge */
  margin-top: 16px;
  margin-bottom: -12px;

  &:hover {
    background: rgba(255,255,255,0.14);
    border-color: rgba(255,255,255,0.4);
    color: rgba(255, 255, 255,1);
  }

  @media (max-width: 768px) {
    font-size: 0.72rem;
    padding: 5px 10px;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Modal = styled(motion.div)`
  /* Same treatment as the other modals: translucent theme blue + white streaks */
  background-color: ${colors.base.themePrimaryTransparent};
  background-image:
    linear-gradient(73deg,  transparent calc(47% - 1px), rgba(255,255,255,0.14) 47%, transparent calc(47% + 1px)),
    linear-gradient(161deg, transparent calc(38% - 1px), rgba(255,255,255,0.10) 38%, transparent calc(38% + 1px)),
    linear-gradient(210deg, transparent calc(55% - 1px), rgba(255,255,255,0.08) 55%, transparent calc(55% + 1px)),
    linear-gradient(30deg,  transparent calc(68% - 1px), rgba(255,255,255,0.06) 68%, transparent calc(68% + 1px));
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 24px;
  box-shadow: 0 16px 60px rgba(0,0,0,0.5);
  padding: 24px;
  width: min(680px, 100%);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 20px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  color: ${colors.accent.primary};
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconBtn = styled.button`
  background: none;
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 50%;
  width: 26px;
  height: 26px;
  color: rgba(255,255,255,0.6);
  font-size: 0.72rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  &:hover { color: white; border-color: white; }
`;

const InfoBox = styled(motion.div)`
  background: rgba(0,0,0,0.4);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 12px;
  padding: 10px 14px;
  color: rgba(255, 255, 255,0.8);
  font-size: 0.78rem;
  line-height: 1.5;
  flex-shrink: 0;
`;

const FilterSection = styled.div`
  flex-shrink: 0;
`;

const FilterLabel = styled.div`
  color: rgba(255, 255, 255,0.5);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const FilterLine = styled.div`
  width: 14px;
  height: 2px;
  background: rgba(255,255,255,0.15);
  flex-shrink: 0;
`;

const FilterCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid ${props => props.$filled ? colors.base.accentPrimary : 'rgba(255,255,255,0.2)'};
  background: ${props => props.$filled ? 'rgba(255, 255, 255,0.1)' : 'rgba(255,255,255,0.04)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;

  &:hover {
    border-color: ${props => props.$filled ? colors.accent.primaryHighlight : 'rgba(255,255,255,0.4)'};
    background: ${props => props.$filled ? 'rgba(255, 255, 255,0.18)' : 'rgba(255,255,255,0.08)'};
  }
`;

const FilterCircleLabel = styled.span`
  color: rgba(255,255,255,0.3);
  font-size: 0.85rem;
`;

const ClearFilterBtn = styled.button`
  margin-left: 10px;
  background: none;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 12px;
  padding: 4px 10px;
  color: rgba(255,255,255,0.5);
  font-size: 0.72rem;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover { color: white; border-color: white; }
`;

const SearchOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  z-index: 21000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const SearchBox = styled(motion.div)`
  background: ${colors.base.themePrimaryTransparent};
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 20px;
  padding: 20px;
  width: min(520px, 100%);
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SearchInput = styled.input`
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 12px;
  padding: 10px 14px;
  color: rgba(255, 255, 255,0.95);
  font-size: 0.9rem;
  outline: none;
  width: 100%;

  &::placeholder { color: rgba(255,255,255,0.3); }
  &:focus { border-color: rgba(255,255,255,0.5); }
`;

const SearchResults = styled.div`
  overflow-y: auto;
  max-height: 42vh;
  display: flex;
  flex-direction: column;
  gap: 4px;
  -webkit-overflow-scrolling: touch;
`;

const SearchResultItem = styled.div`
  padding: 9px 14px;
  border-radius: 10px;
  color: rgba(255, 255, 255,0.85);
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover { background: rgba(255,255,255,0.12); color: white; }
`;

const MiniSvg = styled.div`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  svg { width: 100%; height: 100%; }
`;

const ListSection = styled.div`
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  -webkit-overflow-scrolling: touch;
  padding-right: 4px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,181,71,0.4); border-radius: 3px; }
`;

const ComboCard = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 14px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;

  /* Mobile: count badge floats in the top-right corner, so leave room for it
     and give the chain the full row width */
  @media (max-width: 768px) {
    padding: 24px 10px 10px;
  }
`;

const ComboChain = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
  flex-wrap: nowrap;
  overflow-x: auto;
  /* Swipe to scroll without visible scrollbars — they steal row height on
     mobile and trigger a vertical scrollbar inside the card */
  @media (max-width: 768px) {
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

const CombLine = styled.div`
  width: 10px;
  height: 2px;
  background: rgba(255,255,255,0.2);
  flex-shrink: 0;
`;

const ComboCircle = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid ${colors.base.accentPrimary};
  background: rgba(255, 255, 255,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: ${colors.accent.primaryHighlight};
    background: rgba(255, 255, 255,0.18);
  }
`;

const PeekBtn = styled.button`
  display: none; /* desktop has hover previews */
  position: absolute;
  bottom: -7px;
  right: -7px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(0,0,0,0.6);
  color: white;
  line-height: 1;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 2;

  @media (hover: none), (max-width: 768px) {
    display: flex;
  }
`;

const CountBadge = styled.div`
  background: rgba(255, 255, 255,0.12);
  border: 1px solid rgba(255, 255, 255,0.3);
  border-radius: 12px;
  padding: 4px 10px;
  color: ${colors.accent.primary};
  font-size: 0.78rem;
  font-weight: 700;
  flex-shrink: 0;
  white-space: nowrap;

  /* Mobile: number only ("1"), floated to the card's top-right corner so the
     chain (sun + circles + zzz) gets the whole row width */
  @media (max-width: 768px) {
    .count-label { display: none; }
    position: absolute;
    top: 4px;
    right: 8px;
    padding: 1px 7px;
    font-size: 0.7rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: rgba(255, 255, 255,0.35);
  font-size: 0.85rem;
  padding: 32px 0;
`;

// ── Component ────────────────────────────────────────────────────────────────
const CommunitySolutionsPopup = ({ allNodes = [], onNavigateToNode }) => {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');
  const pickLang = (en, fr) => (isFr ? (fr || en) : (en || fr)) || '';
  const [open, setOpen] = useState(false);
  const [combinations, setCombinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  // filters: array of 5 slots, each null or a node object
  const [filters, setFilters] = useState([null, null, null, null, null]);
  const [searchSlot, setSearchSlot] = useState(null); // index of slot being searched
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredKey, setHoveredKey] = useState(null); // "comboIdx-nodeIdx"
  const [peeked, setPeeked] = useState(false); // preview opened via eye tap (mobile)
  const [hoveredNodeData, setHoveredNodeData] = useState(null);
  const [hoveredRect, setHoveredRect] = useState(null);
  const searchInputRef = useRef(null);

  useEscapeToClose(open, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getCommunitySolutions()
      .then(data => setCombinations(data.combinations || []))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (searchSlot !== null && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchSlot]);

  const openSearchForSlot = (idx) => {
    setSearchSlot(idx);
    setSearchQuery('');
  };

  const clearFilter = (idx) => {
    setFilters(prev => { const f = [...prev]; f[idx] = null; return f; });
  };

  const clearAllFilters = () => setFilters([null, null, null, null, null]);
  const clearHover = () => { setHoveredKey(null); setHoveredNodeData(null); setHoveredRect(null); setPeeked(false); };

  // Tap-to-preview (eye): close when tapping anywhere outside an eye button
  useEffect(() => {
    if (!peeked || !hoveredNodeData) return;
    const onDocPointerDown = (e) => {
      if (e.target.closest && e.target.closest('[data-peek]')) return;
      clearHover();
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [peeked, hoveredNodeData]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectNodeForFilter = (node) => {
    if (searchSlot === null) return;
    setFilters(prev => { const f = [...prev]; f[searchSlot] = node; return f; });
    setSearchSlot(null);
  };

  // Filter combinations: a combo matches if it contains all selected filter nodes (any position)
  const activeFilters = filters.filter(Boolean);
  const filtered = combinations.filter(combo => {
    if (activeFilters.length === 0) return true;
    const comboIds = new Set(combo.nodes.map(n => n.nodeId));
    return activeFilters.every(f => comboIds.has(f.id));
  });

  const searchResults = searchQuery.trim().length > 0
    ? allNodes
        .filter(n => n.title?.toLowerCase().includes(searchQuery.toLowerCase()))
        .slice(0, 30)
    : allNodes.slice(0, 20);

  const hasActiveFilters = activeFilters.length > 0;

  return (
    <>
      <TriggerBtn onClick={() => setOpen(true)}>
        <IconCommunity size={15} /> {t('solutions.communityIdeas')}
      </TriggerBtn>

      {createPortal(
        <AnimatePresence>
          {open && (
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
            >
              <Modal
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
              >
                <ModalHeader>
                  <ModalTitle>{t('solutions.communityIdeasTitle')}</ModalTitle>
                  <HeaderActions>
                    <IconBtn onClick={() => setShowInfo(v => !v)}><IconInfo size={13} /></IconBtn>
                    <IconBtn onClick={() => setOpen(false)} style={{ fontSize: '0.85rem' }}>✕</IconBtn>
                  </HeaderActions>
                </ModalHeader>

                <AnimatePresence>
                  {showInfo && (
                    <InfoBox
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      {t('solutions.communityIdeasInfo')}
                    </InfoBox>
                  )}
                </AnimatePresence>

                {/* Filter circles */}
                <FilterSection>
                  <FilterLabel>
                    {t('solutions.filterByIdea')}
                    {hasActiveFilters && (
                      <ClearFilterBtn onClick={clearAllFilters}>{t('solutions.clearFilters')}</ClearFilterBtn>
                    )}
                  </FilterLabel>
                  <FilterRow>
                    {filters.map((f, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <FilterLine />}
                        <div style={{ position: 'relative' }}>
                          <FilterCircle $filled={!!f} onClick={() => f ? clearFilter(i) : openSearchForSlot(i)}>
                            {f ? (
                              <CardArt id={f.id} title={f.title} />
                            ) : (
                              <FilterCircleLabel>+</FilterCircleLabel>
                            )}
                          </FilterCircle>
                        </div>
                      </React.Fragment>
                    ))}
                  </FilterRow>
                </FilterSection>

                {/* Combinations list */}
                <ListSection>
                  {loading && <EmptyState>{t('common.loading')}</EmptyState>}
                  {!loading && filtered.length === 0 && (
                    <EmptyState>{t('solutions.noCommunityIdeas')}</EmptyState>
                  )}
                  {!loading && filtered.map((combo, ci) => (
                    <ComboCard key={ci}>
                      <ComboChain>
                        <SunIcon />
                        <CombLine />
                        {combo.nodes.map((node, ni) => {
                          const hKey = `${ci}-${ni}`;
                          const nodeTitle = pickLang(node.titleEn, node.titleFr);
                          const nodeDesc  = pickLang(node.shortDescriptionEn, node.shortDescriptionFr);
                          return (
                            <React.Fragment key={ni}>
                              {ni > 0 && <CombLine />}
                              <div style={{ position: 'relative' }}>
                                <ComboCircle
                                  onClick={() => { clearHover(); onNavigateToNode && onNavigateToNode(node.nodeId); setOpen(false); }}
                                  onMouseEnter={(e) => {
                                    setHoveredKey(hKey);
                                    setHoveredRect(e.currentTarget.getBoundingClientRect());
                                    setHoveredNodeData({
                                      id: node.nodeId,
                                      title: nodeTitle,
                                      colorCode: node.colorCode,
                                      shortDescription: nodeDesc,
                                    });
                                  }}
                                  onMouseLeave={() => { setHoveredKey(null); setHoveredNodeData(null); setHoveredRect(null); }}
                                >
                                  <CardArt id={node.nodeId} title={nodeTitle} />
                                </ComboCircle>
                                <PeekBtn
                                  data-peek
                                  title={nodeTitle}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (hoveredKey === hKey) { clearHover(); return; }
                                    setPeeked(true);
                                    setHoveredKey(hKey);
                                    setHoveredRect(e.currentTarget.parentElement.getBoundingClientRect());
                                    setHoveredNodeData({
                                      id: node.nodeId,
                                      title: nodeTitle,
                                      colorCode: node.colorCode,
                                      shortDescription: nodeDesc,
                                    });
                                  }}
                                >
                                  <IconEye size={12} />
                                </PeekBtn>
                              </div>
                            </React.Fragment>
                          );
                        })}
                        <CombLine />
                        <ZzzIcon />
                      </ComboChain>
                      <CountBadge>
                        {combo.count}
                        <span className="count-label"> {combo.count === 1 ? t('solutions.person') : t('solutions.people')}</span>
                      </CountBadge>
                    </ComboCard>
                  ))}
                </ListSection>
              </Modal>
            </Overlay>
          )}

          {/* Card search overlay for filter selection */}
          {searchSlot !== null && (
            <SearchOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => { if (e.target === e.currentTarget) setSearchSlot(null); }}
            >
              <SearchBox
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
              >
                <div style={{ color: colors.accent.primary, fontWeight: 700, fontSize: '0.9rem' }}>
                  {t('solutions.selectCard')}
                </div>
                <SearchInput
                  ref={searchInputRef}
                  placeholder={t('tree.searchPlaceholder')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                <SearchResults>
                  {searchResults.map(node => (
                    <SearchResultItem key={node.id} onClick={() => selectNodeForFilter(node)}>
                      <MiniSvg>
                        <CardArt id={node.id} title={node.title} />
                      </MiniSvg>
                      {node.title}
                    </SearchResultItem>
                  ))}
                </SearchResults>
              </SearchBox>
            </SearchOverlay>
          )}
        </AnimatePresence>,
        document.body
      )}

      {createPortal(
        <AnimatePresence>
          {hoveredNodeData && hoveredRect && (
            <NodeCardPreview
              nodeData={hoveredNodeData}
              anchorRect={hoveredRect}
              allNodes={allNodes}
              onClose={peeked ? clearHover : undefined}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default CommunitySolutionsPopup;
